import { useEffect } from 'react'
import SubHeaders from '@/components/Headers/SubHeaders'
import useTabs from '@/hooks/useTabs'
import { paths } from '@/utils/path'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Tabs } from 'vnetwork-ui'
import { CustomerPipelineFilters } from './types'
import Filters from './components/Filters'
import DailyInputChart from './components/DailyInputChart'
import ConversionFunnelChart from './components/ConversionFunnelChart'
import StatusTransitionsChart from './components/StatusTransitionsChart'
import BestEmployees from './components/BestEmployees'
import QualifiedProspectsTab from './components/QualifiedProspects'
import dayjs from 'dayjs'
import PipelineOverviewChart from './components/PipelineOverviewChart'
import CustomerSummaryCards from './components/CustomerSummaryCards'
import IndustryRadarChart from './components/IndustryRadarChart'
import ProductBarChart from './components/ProductBarChart'
import EmployeeContactsTable from './components/EmployeeContactsTable'
import { useEmployeeContactsData } from './hooks/useEmployeeContactsData'
import { useSalePerformancesTour } from './hooks/use-sale-performances-tour'
import { useTourHandler } from '@/contexts/tour-handler'

// Overview Tab Component
const OverviewTab: React.FC<{ filters: CustomerPipelineFilters }> = ({
    filters,
}) => {
    const hasSalesFilter = !!filters.sales_manager_codes
    const {
        employeeData,
        previousEmployeeData,
        totalContacts,
        previousTotalContacts,
    } = useEmployeeContactsData(filters)

    return (
        <div className="space-y-2">
            {/* Summary Stats Cards */}
            <div data-tour="sp-summary-cards">
                <CustomerSummaryCards filters={filters} />
            </div>

            {/* Radar Chart + Daily Input Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-2 min-h-[340px]">
                <div data-tour="sp-industry-radar">
                    <IndustryRadarChart filters={filters} />
                </div>
                <div className="col-span-3" data-tour="sp-daily-input">
                    <DailyInputChart filters={filters} viewTable={false} />
                </div>
            </div>
            <div
                className="bg-white rounded-md p-4 shadow-sm border border-gray-100"
                data-tour="sp-employee-contacts"
            >
                <EmployeeContactsTable
                    data={employeeData}
                    previousData={previousEmployeeData}
                    totalContacts={totalContacts}
                    previousTotalContacts={previousTotalContacts}
                    maxHeight="200px"
                />
            </div>
            {/* Conversion Funnel + Pipeline Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                <div data-tour="sp-conversion-funnel">
                    <ConversionFunnelChart filters={filters} />
                </div>
                <div data-tour="sp-pipeline-overview">
                    <PipelineOverviewChart filters={filters} />
                </div>
                <div
                    className="col-span-1 md:col-span-2"
                    data-tour="sp-product-bar"
                >
                    <ProductBarChart filters={filters} />
                </div>
            </div>

            {/* Best Employees - chỉ hiển thị khi không có filter sales */}
            {!hasSalesFilter && (
                <div data-tour="sp-best-employees">
                    <BestEmployees filters={filters} />
                </div>
            )}

            {/* Status Transitions */}
            <div data-tour="sp-status-transitions">
                <StatusTransitionsChart filters={filters} />
            </div>
        </div>
    )
}

const SalePerformances = () => {
    const { t } = useTranslation()
    const { startTour } = useSalePerformancesTour()
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Register tour handler for keyboard shortcut
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    const { control, watch, setValue } = useForm<CustomerPipelineFilters>({
        defaultValues: {
            gte: dayjs()
                .subtract(1, 'month')
                .startOf('month')
                .format('YYYY-MM-DD'),
            lte: dayjs().endOf('month').format('YYYY-MM-DD'),
            sales_manager_codes: undefined,
            product_group_names: undefined,
            company_sizes: undefined,
        },
    })

    const filters = watch()

    const { TabComponent, TabProps } = useTabs({
        tabs: [
            {
                label: t('overview.label'),
                value: 'overview',
                component: <OverviewTab filters={filters} />,
            },
            {
                label: t('qualified_prospects.title'),
                value: 'qualified-prospects',
                component: <QualifiedProspectsTab filters={filters} />,
            },
        ],
    })

    return (
        <div className="">
            <SubHeaders
                className="mb-2"
                breadcrumbs={[
                    {
                        title: t('reports'),
                        path: '/reports',
                    },
                    {
                        title: t('customer_pipeline'),
                        path: paths.reportCustomer,
                    },
                ]}
                actions={[]}
            />

            {/* Filters */}
            <div data-tour="sp-filters">
                <Filters control={control} setValue={setValue} />
            </div>

            {/* Tabs */}
            <div className="space-y-2">
                <Tabs {...TabProps} />
                {TabComponent}
            </div>
        </div>
    )
}

export default SalePerformances
